import { useState, useMemo, ReactNode } from 'react';
import { Button } from '@startuptools/ui/Button';
import { Table } from '@startuptools/ui/Table';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Card } from '@startuptools/ui/Card';
import { Switch } from '@startuptools/ui/Switch';
import { Trans } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { DATE_FORMAT_LUXON } from '@startuptools/common/common';
import { rangeCount } from '@startuptools/common/transactions';
import { useShareholdersQuery, useSnrShareCapitalChangesQuery } from '../../../graphql/react-operations';
import { stgsToStsPlus } from '../../../helpers/extended-transaction';
import { apiClient } from '../../../common/http';
import { DownloadFile } from '../../../common/DownloadFile';
import { TransactionRow } from './TransactionRow';
import { DateTime } from 'luxon';
import { type GqlTransactionKind } from '../../../graphql/base-types.graphql';
import { StgLabel } from '../transactions/StgLabel';
import { useTransactionSuspenseQuery } from './Transaction.graphql';

export interface TableRowData {
  transactionId: string;
  date: string;
  transactionKind: GqlTransactionKind;
  sortOrder: number;
  buyerIdentityId?: string;
  sellerIdentityId?: string;
  label: ReactNode;
  comment?: string | null;
  numberOfShares: number;
  shareClass: {
    from?: string;
    to?: string;
  };
  pricePerShare?: string | null;
  blockStart: number;
  blockEnd: number;
  currency: string;
}

export const TransactionReport = () => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [showShareRange, setShowShareRange] = useState(false);
  const [showComments, setShowComments] = useState(true);

  const companyId = useCompanyId();

  const {
    data: { shareTransactionGroups, company, currentUser },
    error: stgsError,
    refetch: stgsRefetch,
  } = useTransactionSuspenseQuery({ variables: { companyId }, fetchPolicy: 'no-cache' });

  const { data: shData } = useShareholdersQuery({ variables: { companyId } });

  const { data: snrData, loading: snrLoading } = useSnrShareCapitalChangesQuery({ variables: { companyId } });

  const tableRows = useMemo(() => {
    if (snrData && !snrLoading) {
      const stPlusData = stgsToStsPlus(shareTransactionGroups);

      const rows = stPlusData.map(stp => {
        const matchedSnrChange = snrData?.snrShareCapitalChanges.find(snr => snr.id === stp.snrShareCapitalChangeId);

        return {
          transactionId: stp.id,
          date: stp.date.toFormat(DATE_FORMAT_LUXON),
          transactionKind: stp.kind,
          sortOrder: stp.sortOrder,
          buyerIdentityId: stp.buyerIdentityId,
          sellerIdentityId: stp.sellerIdentityid,
          label: (
            <StgLabel
              stg={{ kind: stp.kind, transactionDate: stp.date.toLocaleString(DateTime.DATE_SHORT) }}
              snrScc={matchedSnrChange}
            />
          ),
          comment: stp.comment,
          numberOfShares: rangeCount(stp.blockStart, stp.blockEnd),
          shareClass: {
            from: stp.shareClassFrom?.name,
            to: stp.shareClassTo?.name,
          },
          pricePerShare: stp.pricePerShare,
          blockStart: stp.blockStart,
          blockEnd: stp.blockEnd,
          currency: stp.currency,
        } satisfies TableRowData;
      });

      return rows;
    }
    return [];
  }, [shareTransactionGroups, snrData, snrLoading]);

  const showAvatar = useMemo(() => (shData?.shareholders?.length || 0) <= 50, [shData?.shareholders]);

  const handleExcelDownload = async () => {
    const url = await apiClient.getTransactionsReportExcelExportUrl(companyId, showShareRange);
    setBlobUrl(url);
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography level="h2">
          <Trans id="Transaction Report" />
        </Typography>
        <Stack direction="row" gap={2}>
          {currentUser.admin && (
            <Switch
              checked={showShareRange}
              onChange={event => setShowShareRange(event.currentTarget.checked)}
              endDecorator={<Trans id="Show share range" />}
            />
          )}
          <Switch
            checked={showComments}
            onChange={event => setShowComments(event.currentTarget.checked)}
            endDecorator={<Trans id="Show comments" />}
          />
          <Button onClick={handleExcelDownload}>
            <Trans id="Export as Excel" />
          </Button>
        </Stack>
      </Stack>
      <Card>
        {stgsError ? (
          <Alert variant="outlined" color="danger">
            <Trans id="An error occurred. Please refresh the page. If the error persists, contact our customer support" />
          </Alert>
        ) : (
          <Table sx={{ tableLayout: 'auto' }} noWrap>
            <thead>
              <tr>
                <th>
                  <Trans id="Date" />
                </th>
                <th>
                  <Trans id="Transaction Type" />
                </th>
                <th>
                  <Trans id="Buyer" />
                </th>
                <th>
                  <Trans id="Seller" />
                </th>
                <th>
                  <Trans id="Share Class" />
                </th>
                <th align="right">
                  <Trans id="Shares" />
                </th>
                {showShareRange && (
                  <>
                    <th align="right">
                      <Trans id="Block start" />
                    </th>
                    <th align="right">
                      <Trans id="Block end" />
                    </th>
                  </>
                )}
                <th align="right">
                  <Trans id="Price per Share" />
                </th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map(row => (
                <TransactionRow
                  key={row.transactionId}
                  row={row}
                  showAvatar={showAvatar}
                  showShareRange={showShareRange}
                  showComments={showComments}
                  onRefetch={stgsRefetch}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {!!blobUrl && (
        <DownloadFile
          url={blobUrl}
          fileName={`Transaktionsrapport_${company.name}_${DateTime.local().toISODate()}.xlsx`}
          onClose={() => {
            window.URL.revokeObjectURL(blobUrl);
            setBlobUrl(null);
          }}
        />
      )}
    </Stack>
  );
};
